import { isJudgementTone, type JudgementTone } from '@/design-system';

import type {
    FullReportChapterResource,
    FullReportResource,
    PublicReportResource,
    PublicTenantResource,
    ReportTextResource,
} from './types';

/**
 * De openbare rapportpagina: alles wat een koper *mag* zien.
 *
 * De schermen kennen alleen de vorm in dit bestand; `toPublicReport` is de
 * enige plek waar de lijnvorm uit `types.ts` wordt vertaald.
 */

/**
 * De talen waarin de omlijsting van de pagina bestaat. Welke de koper echt kan
 * kiezen, staat in `tenant.languages`: alleen `nl` zolang de server niet vertaalt
 * (geen vertaalsleutel), anders ook de talen waarin de server het rapport vertaalt.
 */
export const PUBLIC_LANGUAGES = ['nl', 'en', 'de', 'fr', 'es', 'pl', 'tr', 'ar'] as const;

export type PublicLanguage = (typeof PUBLIC_LANGUAGES)[number];

export function isPublicLanguage(value: string): value is PublicLanguage {
    return (PUBLIC_LANGUAGES as readonly string[]).includes(value);
}

/** Talen die van rechts naar links lezen. */
const RTL_LANGUAGES: readonly PublicLanguage[] = ['ar'];

export function languageDirection(language: PublicLanguage): 'ltr' | 'rtl' {
    return RTL_LANGUAGES.includes(language) ? 'rtl' : 'ltr';
}

/**
 * Hoe ver de vertaling is waar de koper om vroeg: `pending` (de server vertaalt,
 * een paar seconden), `ready`, of `failed` (de koper leest het Nederlands).
 */
export const TRANSLATION_STATUSES = ['pending', 'ready', 'failed'] as const;

export type TranslationStatus = (typeof TRANSLATION_STATUSES)[number];

export interface PublicTranslation {
    language: PublicLanguage;
    status: TranslationStatus;
}

export interface PublicTenant {
    name: string;
    logoUrl: string | null;
    /** Accentkleur als CSS-kleur; wordt als custom property op de pagina gezet. */
    accentColor: string | null;
    /** De tekst op die accentkleur; de API heeft hem al leesbaar gemaakt. */
    accentTextColor: string | null;
    /** De rustige tweede kleur voor koppen; `null` laat de pagina haar eigen inkt houden. */
    headingColor: string | null;
    phone: string | null;
    email: string | null;
    /** De echte url, `https://www.keurhuis-voorbeeld.nl`; de pagina toont hem korter. */
    website: string | null;
    /** `["Voorbeeldweg 12A", "3512 AB Utrecht"]`, zoals het rapport het adres drukt; leeg zonder adres. */
    addressLines: string[];
    /** "ma–vr 8–18 uur". */
    openingHours: string | null;
    kvk: string | null;
    memberships: string[];
    languages: PublicLanguage[];
}

export const COST_KEYS = ['direct', 'termijn', 'onderhoud'] as const;

export type CostKey = (typeof COST_KEYS)[number];

export interface PublicCostBlock {
    key: CostKey;
    amount: number;
    /** Aantal onderdelen achter dit bedrag; `null`: geen voetnoot tonen. */
    count: number | null;
}

export interface PublicCosts {
    /** Het bedrijf kan de kosten voor de koper verbergen. */
    visible: boolean;
    blocks: PublicCostBlock[];
    total: number | null;
}

/** Eén regel van de NHG-bijlage met kosten. */
export interface PublicNhgRow {
    code: string;
    element: string;
    /** De hoofdstukken van het rapport waar het gebrek staat. */
    locations: string[];
    direct: number;
    termijn: number;
}

/**
 * De NHG-bijlage voor de koper: de regels van het Model bouwkundig rapport NHG
 * waarop kosten staan, met de totalen, zoals in de PDF.
 */
export interface PublicNhg {
    tables: Array<{
        key: 'general' | 'storeys';
        label: string;
        rows: PublicNhgRow[];
        total: { direct: number; termijn: number };
    }>;
    total: { direct: number; termijn: number };
    improvement: number;
    /** Kosten uit het rapport die niet in de bijlage staan. */
    outside: number;
}

export const PUBLIC_SEVERITIES = ['goed', 'aandacht', 'gebrek'] as const;

export type PublicSeverity = (typeof PUBLIC_SEVERITIES)[number];

export interface PublicFinding {
    id: string;
    title: string;
    /** Bepaalt de urgentiepil; dezelfde drie sleutels als de kostenblokken. */
    urgency: CostKey;
    /** In kopers-taal, nooit normtaal. */
    explanation: string;
    /** Waar het gebrek zit, in de woorden van de inspecteur; `null` als er niets staat. */
    location: string | null;
    section: string;
    /** `null` als het bedrijf de kosten verbergt. */
    amount: number | null;
    photoUrl: string | null;
    photoCaption: string | null;
}

export interface PublicPart {
    number: string;
    label: string;
    detail: string | null;
    /** "Matig" - het woord, nooit het getal; `null` zonder oordeel. */
    judgement: string | null;
    /** De kleur van dat oordeel, overal in het rapport dezelfde. */
    tone: JudgementTone | null;
    /** `null` voor een onderdeel zonder oordeel en zonder bevindingen, zoals alleen een toelichting. */
    severity: PublicSeverity | null;
}

export interface PublicDocument {
    key: string;
    label: string;
    /** Werkt zonder sessie; met een toegangscode is het een ondertekende link die een uur werkt. */
    url: string;
    /** "PDF, 24 pagina's". */
    detail: string | null;
}

export interface PublicEnergyLabel {
    code: string;
    usage: number | null;
    advice: string | null;
    pdfUrl: string | null;
}

export interface PublicInspector {
    name: string;
    qualification: string | null;
    responseTime: string | null;
    phone: string | null;
    avatarUrl: string | null;
}

export interface PublicRecipient {
    name: string;
    sharedAt: string | null;
}

export interface PublicSharing {
    /** Het bedrijf kan doorsturen uitzetten. */
    enabled: boolean;
    recipients: PublicRecipient[];
}

export const PUBLIC_INVOICE_STATUSES = ['open', 'paid', 'overdue'] as const;

export type PublicInvoiceStatus = (typeof PUBLIC_INVOICE_STATUSES)[number];

export interface PublicInvoice {
    number: string;
    amount: number;
    status: PublicInvoiceStatus;
    /** Al opgemaakte regel: "Betaald via iDEAL op 3 september". */
    paymentNote: string | null;
    pdfUrl: string | null;
}

export interface PublicFooter {
    disclaimer: string;
    company: string | null;
    kvk: string | null;
    memberships: string[];
}

/** Een stukje van een regel: tekst, of een link (altijd https; de API liet alleen veilige door). */
export type ReportInline =
    { type: 'text'; text: string } | { type: 'link'; text: string; href: string };

/**
 * Een rapporttekst als boom, zoals de PDF hem drukt: alinea's met regels, en
 * opsommingen. Nooit html.
 */
export type ReportBlock =
    { type: 'paragraph'; lines: ReportInline[][] } | { type: 'list'; items: ReportInline[][] };

export type ReportText = ReportBlock[];

export type CostAmounts = Record<CostKey, number>;

export interface PublicReportPhoto {
    /** De kleine weergave voor in de tekst. */
    thumbUrl: string;
    /** De grote, voor als de koper de foto opent. */
    url: string;
    caption: string | null;
}

/** Een bevinding of opmerking in het volledige rapport. */
export interface PublicReportEntry {
    id: string;
    /** Kort, voor een lijst ("Scheurvorming gevel"); het rapport zelf toont de tekst. */
    title: string;
    text: ReportText;
    location: string | null;
    /** De ernst van de bevinding zelf; `null` zonder oordeel. */
    severity: PublicSeverity | null;
    /** Een opmerking ("De dakbedekking is in goede staat") telt nooit als bevinding. */
    remark: boolean;
    /** Per urgentie met een bedrag; leeg als het bedrijf de kosten verbergt. */
    costs: Array<{ key: CostKey; amount: number }>;
    /** De regel in de NHG-bijlage, zoals in de PDF. */
    nhgCode: string | null;
    photos: PublicReportPhoto[];
}

/** Een kop onder een onderdeel, met het oordeel in woorden. */
export interface PublicReportHeading {
    /** Het anker van een diepe link: `#rapport-<key>`, zoals `#rapport-6-2`. */
    key: string;
    number: string | null;
    title: string;
    /** Hoe het rapport de kop tekent: een sectie (2), of alles daaronder (3). */
    level: 2 | 3;
    /** De echte diepte in de boom (2, 3, 4, …), voor de inspringing in het overzicht. */
    depth: number;
    /** "Matig" - het woord, nooit het getal. */
    judgement: string | null;
    /** De kleur van het oordeel: Goed groen, Voldoende geel, Matig oranje, Onvoldoende rood, Niet waarneembaar blauw. */
    tone: JudgementTone | null;
    severity: PublicSeverity | null;
    entries: PublicReportEntry[];
}

export interface PublicNhgLine {
    label: string;
    direct: number;
    termijn: number;
    total: number;
}

/** De NHG-bijlage zoals in de PDF, zonder de administratieve gegevens. */
export interface PublicNhgAnnex {
    model: string | null;
    intro: string;
    summary: {
        title: string;
        columns: string[];
        rows: PublicNhgLine[];
        total: PublicNhgLine;
        improvement: { label: string; amount: number };
    };
    reconciliation: {
        title: string;
        rows: Array<{ label: string; amount: number }>;
        note: string;
    };
    remarks: { title: string; items: string[] };
    results: Array<{
        title: string;
        note: string | null;
        columns: string[];
        rows: Array<{
            code: string;
            element: string;
            locations: string[];
            action: string;
            share: string;
            direct: number;
            termijn: number;
        }>;
        total: { label: string; direct: number; termijn: number };
    }>;
    legend: { title: string; items: string[] };
    programme: {
        title: string;
        columns: string[];
        rows: Array<{ code: string; title: string; items: string[]; bbl: string[] }>;
    };
}

interface ChapterBase {
    /** Het anker van een diepe link: `#rapport-<key>`. */
    key: string;
    number: string | null;
    title: string;
    /** Staat achteraan, in de bijlage "Toelichting en voorwaarden", zoals in de PDF. */
    appendix: boolean;
}

/** Een regel van "Zo leest u dit rapport": een label en wat het betekent. */
export interface ReadingGuideItem {
    key: string;
    label: string;
    text: string;
}

/** Een oordeel in "Zo leest u dit rapport", in zijn eigen kleur. */
export interface ReadingGuideJudgement extends ReadingGuideItem {
    tone: JudgementTone;
}

export type PublicReportChapter =
    | (ChapterBase & { kind: 'summary'; text: ReportText })
    | (ChapterBase & {
          kind: 'guide';
          /** `null` als het bedrijf de kosten verbergt. */
          costs: { title: string; items: ReadingGuideItem[] } | null;
          judgements: { title: string; note: string | null; items: ReadingGuideJudgement[] };
      })
    | (ChapterBase & {
          kind: 'costs';
          text: ReportText;
          parts: Array<CostAmounts & { label: string }>;
          total: CostAmounts;
          groups: Array<CostAmounts & { name: string }>;
      })
    | (ChapterBase & {
          kind: 'part';
          judgement: string | null;
          tone: JudgementTone | null;
          severity: PublicSeverity | null;
          /** Wat het onderdeel kost; `null` als het bedrijf de kosten verbergt. */
          costs: CostAmounts | null;
          /** De eigen tekst van kantoor bij dit onderdeel; leeg als die er niet is. */
          note: ReportText;
          entries: PublicReportEntry[];
          headings: PublicReportHeading[];
      })
    | (ChapterBase & { kind: 'nhg'; annex: PublicNhgAnnex })
    | (ChapterBase & {
          kind: 'conditions';
          text: ReportText;
          footerText: string | null;
          company: string;
      });

export type PublicReportChapterKind = PublicReportChapter['kind'];

/** Het voorblad van het volledige rapport. */
export interface PublicReportCover {
    kind: string;
    norm: string | null;
    street: string;
    place: string;
    inspectedAt: string | null;
    inspector: string | null;
    orderNumber: string;
    version: number;
    generatedAt: string | null;
}

/** Het volledige geschreven rapport, met dezelfde inhoud als de PDF. */
export interface PublicFullReport {
    cover: PublicReportCover;
    chapters: PublicReportChapter[];
}

export interface PublicReport {
    orderNumber: string;
    /** De taal waarin de inhoud **is**: het Nederlands, of de vertaling die klaar is. */
    language: PublicLanguage;
    availableLanguages: PublicLanguage[];
    /** De taal waar de koper om vroeg en hoe ver die is; `null` als hij Nederlands leest. */
    translation: PublicTranslation | null;
    tenant: PublicTenant;
    publishedAt: string | null;
    /** Tot wanneer deze link werkt. */
    validUntil: string;
    service: string;
    address: string;
    /** Het adres in de link (`saffier-4-hoorn`); leeg als het rapport geen adres heeft. */
    addressSlug: string;
    summary: string | null;
    documents: PublicDocument[];
    costs: PublicCosts;
    /** `null` zonder bijlage of als de kosten verborgen zijn. */
    nhg: PublicNhg | null;
    findings: PublicFinding[];
    parts: PublicPart[];
    partsNote: string | null;
    energyLabel: PublicEnergyLabel | null;
    inspector: PublicInspector | null;
    sharing: PublicSharing;
    invoice: PublicInvoice | null;
    footer: PublicFooter;
    /** `null` als het volledige rapport niet te lezen was: dan staat alleen het overzicht er. */
    fullReport: PublicFullReport | null;
}

export function toPublicReport(resource: PublicReportResource): PublicReport {
    return {
        orderNumber: resource.order_number,
        language: isPublicLanguage(resource.language) ? resource.language : 'nl',
        availableLanguages: languages(resource.available_languages),
        translation: toTranslation(resource.translation ?? null),
        tenant: toPublicTenant(resource.tenant),
        publishedAt: resource.published_at,
        validUntil: resource.valid_until,
        service: resource.service,
        address: resource.address,
        addressSlug: resource.address_slug,
        summary: resource.summary.trim() === '' ? null : resource.summary,
        documents: resource.documents.map((document) => ({
            key: document.key,
            label: document.label,
            url: document.url,
            detail: document.detail,
        })),
        costs: {
            visible: resource.costs.visible,
            blocks: resource.costs.blocks.flatMap((block) =>
                isCostKey(block.key)
                    ? [{ key: block.key, amount: toAmount(block.amount), count: block.count }]
                    : [],
            ),
            total: resource.costs.total === null ? null : toAmount(resource.costs.total),
        },
        nhg:
            resource.nhg === null
                ? null
                : {
                      tables: resource.nhg.tables.map((table) => ({
                          key: table.key === 'storeys' ? 'storeys' : 'general',
                          label: table.label,
                          rows: table.rows.map((row) => ({
                              code: row.code,
                              element: row.element,
                              locations: row.locations,
                              direct: toAmount(row.direct),
                              termijn: toAmount(row.termijn),
                          })),
                          total: {
                              direct: toAmount(table.total.direct),
                              termijn: toAmount(table.total.termijn),
                          },
                      })),
                      total: {
                          direct: toAmount(resource.nhg.total.direct),
                          termijn: toAmount(resource.nhg.total.termijn),
                      },
                      improvement: toAmount(resource.nhg.improvement),
                      outside: toAmount(resource.nhg.outside),
                  },
        findings: resource.findings.map((finding) => ({
            id: finding.id,
            title: finding.title,
            urgency: isCostKey(finding.urgency) ? finding.urgency : 'onderhoud',
            explanation: finding.explanation,
            location: finding.location,
            section: finding.section,
            amount: finding.amount === null ? null : toAmount(finding.amount),
            photoUrl: finding.photo_url,
            photoCaption: finding.photo_caption,
        })),
        parts: resource.parts.map((part) => ({
            number: part.number,
            label: part.label,
            detail: part.detail,
            judgement: part.judgement ?? null,
            tone: toTone(part.tone),
            severity:
                part.severity !== null &&
                (PUBLIC_SEVERITIES as readonly string[]).includes(part.severity)
                    ? (part.severity as PublicSeverity)
                    : null,
        })),
        partsNote: resource.parts_note,
        energyLabel:
            resource.energy_label === null
                ? null
                : {
                      code: resource.energy_label.code,
                      usage: resource.energy_label.usage,
                      advice: resource.energy_label.advice,
                      pdfUrl: resource.energy_label.pdf_url,
                  },
        inspector:
            resource.inspector === null
                ? null
                : {
                      name: resource.inspector.name,
                      qualification: resource.inspector.qualification,
                      responseTime: resource.inspector.response_time,
                      phone: resource.inspector.phone,
                      avatarUrl: resource.inspector.avatar_url,
                  },
        sharing: {
            enabled: resource.sharing.enabled,
            recipients: resource.sharing.recipients.map((recipient) => ({
                name: recipient.name,
                sharedAt: recipient.shared_at,
            })),
        },
        invoice:
            resource.invoice === null
                ? null
                : {
                      number: resource.invoice.number,
                      amount: toAmount(resource.invoice.amount),
                      status: (PUBLIC_INVOICE_STATUSES as readonly string[]).includes(
                          resource.invoice.status,
                      )
                          ? (resource.invoice.status as PublicInvoiceStatus)
                          : 'open',
                      paymentNote: resource.invoice.payment_note,
                      pdfUrl: resource.invoice.pdf_url,
                  },
        footer: {
            disclaimer: resource.footer.disclaimer,
            company: resource.footer.company,
            kvk: resource.footer.kvk,
            memberships: resource.footer.memberships,
        },
        fullReport: resource.full_report === null ? null : toFullReport(resource.full_report),
    };
}

function toFullReport(resource: FullReportResource): PublicFullReport {
    return {
        cover: {
            kind: resource.cover.kind,
            norm: resource.cover.norm,
            street: resource.cover.street,
            place: resource.cover.place,
            inspectedAt: resource.cover.inspected_at,
            inspector: resource.cover.inspector,
            orderNumber: resource.cover.order_number,
            version: resource.cover.version,
            generatedAt: resource.cover.generated_at,
        },
        chapters: resource.chapters.map(toChapter),
    };
}

function toChapter(chapter: FullReportChapterResource): PublicReportChapter {
    const base = {
        key: chapter.key,
        number: chapter.number,
        title: chapter.title,
        appendix: chapter.appendix ?? false,
    };

    switch (chapter.kind) {
        case 'summary':
            return { ...base, kind: 'summary', text: toText(chapter.text) };
        case 'guide':
            return {
                ...base,
                kind: 'guide',
                costs: chapter.costs,
                judgements: {
                    title: chapter.judgements.title,
                    note: chapter.judgements.note,
                    /* Een oordeel zonder bekende kleur valt weg: een grijs label zou niets leren. */
                    items: chapter.judgements.items.flatMap((item) => {
                        const itemTone = toTone(item.tone);

                        return itemTone === null ? [] : [{ ...item, tone: itemTone }];
                    }),
                },
            };
        case 'costs':
            return {
                ...base,
                kind: 'costs',
                text: toText(chapter.text),
                parts: chapter.parts.map((part) => ({ label: part.label, ...toCostAmounts(part) })),
                total: toCostAmounts(chapter.total),
                groups: chapter.groups.map((group) => ({
                    name: group.name,
                    ...toCostAmounts(group),
                })),
            };
        case 'part':
            return {
                ...base,
                kind: 'part',
                judgement: chapter.judgement,
                tone: toTone(chapter.tone),
                severity: toSeverity(chapter.severity),
                costs: chapter.costs === null ? null : toCostAmounts(chapter.costs),
                note: toText(chapter.note),
                entries: chapter.entries.map(toEntry),
                headings: chapter.headings.map((heading, position) => ({
                    key: heading.key ?? `${chapter.key}-kop-${position + 1}`,
                    number: heading.number,
                    title: heading.title,
                    level: heading.level >= 3 ? 3 : 2,
                    depth: Math.max(2, Math.trunc(heading.level)),
                    judgement: heading.judgement,
                    tone: toTone(heading.tone),
                    severity: toSeverity(heading.severity),
                    entries: heading.entries.map(toEntry),
                })),
            };
        case 'nhg':
            return {
                ...base,
                kind: 'nhg',
                annex: {
                    model: chapter.model,
                    intro: chapter.intro,
                    summary: {
                        title: chapter.summary.title,
                        columns: chapter.summary.columns,
                        rows: chapter.summary.rows.map(toNhgLine),
                        total: toNhgLine(chapter.summary.total),
                        improvement: {
                            label: chapter.summary.improvement.label,
                            amount: toAmount(chapter.summary.improvement.amount),
                        },
                    },
                    reconciliation: {
                        title: chapter.reconciliation.title,
                        rows: chapter.reconciliation.rows.map((row) => ({
                            label: row.label,
                            amount: toAmount(row.amount),
                        })),
                        note: chapter.reconciliation.note,
                    },
                    remarks: chapter.remarks,
                    results: chapter.results.map((table) => ({
                        title: table.title,
                        note: table.note,
                        columns: table.columns,
                        rows: table.rows.map((row) => ({
                            code: row.code,
                            element: row.element,
                            locations: row.locations,
                            action: row.action,
                            share: row.share,
                            direct: toAmount(row.direct),
                            termijn: toAmount(row.termijn),
                        })),
                        total: {
                            label: table.total.label,
                            direct: toAmount(table.total.direct),
                            termijn: toAmount(table.total.termijn),
                        },
                    })),
                    legend: chapter.legend,
                    programme: chapter.programme,
                },
            };
        case 'conditions':
            return {
                ...base,
                kind: 'conditions',
                text: toText(chapter.text),
                footerText: chapter.footer_text,
                company: chapter.company,
            };
    }
}

type EntryResource = Extract<FullReportChapterResource, { kind: 'part' }>['entries'][number];

function toEntry(entry: EntryResource): PublicReportEntry {
    return {
        id: entry.id,
        title: entry.title,
        text: toText(entry.text),
        location: entry.location,
        severity: toSeverity(entry.severity),
        remark: entry.remark,
        costs: entry.costs.flatMap((cost) =>
            isCostKey(cost.key) ? [{ key: cost.key, amount: toAmount(cost.amount) }] : [],
        ),
        nhgCode: entry.nhg_code,
        photos: entry.photos.map((photo) => ({
            thumbUrl: photo.thumb_url,
            url: photo.url,
            caption: photo.caption,
        })),
    };
}

/** De lijnvorm is al dezelfde boom; alleen de typen worden hier vastgelegd. */
function toText(text: ReportTextResource): ReportText {
    return text;
}

function toCostAmounts(amounts: {
    direct: string;
    termijn: string;
    onderhoud: string;
}): CostAmounts {
    return {
        direct: toAmount(amounts.direct),
        termijn: toAmount(amounts.termijn),
        onderhoud: toAmount(amounts.onderhoud),
    };
}

function toNhgLine(line: {
    label: string;
    direct: string;
    termijn: string;
    total: string;
}): PublicNhgLine {
    return {
        label: line.label,
        direct: toAmount(line.direct),
        termijn: toAmount(line.termijn),
        total: toAmount(line.total),
    };
}

function toTone(value: string | null | undefined): JudgementTone | null {
    return isJudgementTone(value) ? value : null;
}

function toSeverity(value: string | null): PublicSeverity | null {
    return value !== null && (PUBLIC_SEVERITIES as readonly string[]).includes(value)
        ? (value as PublicSeverity)
        : null;
}

export function toPublicTenant(resource: PublicTenantResource): PublicTenant {
    return {
        name: resource.name,
        logoUrl: resource.logo_url,
        accentColor: resource.accent_color,
        accentTextColor: resource.accent_text_color ?? null,
        headingColor: resource.heading_color ?? null,
        phone: resource.phone,
        email: resource.email ?? null,
        website: resource.website ?? null,
        addressLines: (resource.address_lines ?? []).filter((line) => line.trim() !== ''),
        openingHours: resource.opening_hours,
        kvk: resource.kvk,
        memberships: resource.memberships,
        languages: languages(resource.languages),
    };
}

export function toTranslation(
    resource: { language: string; status: string } | null,
): PublicTranslation | null {
    if (resource === null || !isPublicLanguage(resource.language)) {
        return null;
    }

    const status = (TRANSLATION_STATUSES as readonly string[]).includes(resource.status)
        ? (resource.status as TranslationStatus)
        : 'failed';

    return { language: resource.language, status };
}

function languages(values: readonly string[]): PublicLanguage[] {
    const known = values.filter(isPublicLanguage);

    return known.length > 0 ? known : ['nl'];
}

function isCostKey(value: string): value is CostKey {
    return (COST_KEYS as readonly string[]).includes(value);
}

/** `"1450.00"` → `1450`. */
function toAmount(value: string): number {
    const amount = Number.parseFloat(value);

    return Number.isFinite(amount) ? amount : 0;
}

